import { useCallback, useEffect, useState } from 'react';

// Flows picked for bulk export. Kept in localStorage so the selection survives
// switching folders, pages and searches while the user collects flows.
const STORAGE_KEY = 'flowExportSelection';

const readStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export function useFlowSelection() {
    const [selectedFlows, setSelectedFlows] = useState(readStorage);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedFlows));
        } catch {
            // Storage is full or unavailable: the selection still works in memory.
        }
    }, [selectedFlows]);

    const isSelected = useCallback(
        (id) => selectedFlows.some((flow) => flow.id === id),
        [selectedFlows]
    );

    const toggleFlow = useCallback((item) => {
        setSelectedFlows((prev) => prev.some((flow) => flow.id === item.id)
            ? prev.filter((flow) => flow.id !== item.id)
            : [...prev, { id: item.id, name: item.name }]);
    }, []);

    const removeFlow = useCallback((id) => {
        setSelectedFlows((prev) => prev.filter((flow) => flow.id !== id));
    }, []);

    // Select every flow currently rendered on the page, or unselect them all
    // when they are already selected.
    const toggleVisible = useCallback((items) => {
        setSelectedFlows((prev) => {
            const allSelected = items.length > 0 && items.every((item) => prev.some((flow) => flow.id === item.id));
            if (allSelected) {
                return prev.filter((flow) => !items.some((item) => item.id === flow.id));
            }
            const added = items
                .filter((item) => !prev.some((flow) => flow.id === item.id))
                .map((item) => ({ id: item.id, name: item.name }));
            return [...prev, ...added];
        });
    }, []);

    const clearSelection = useCallback(() => setSelectedFlows([]), []);

    return { selectedFlows, isSelected, toggleFlow, removeFlow, toggleVisible, clearSelection };
}
